import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Container,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import supabase from "../../supabaseClient";

const applicantFields = [
	{ id: "name", label: "Full Name", type: "text", required: true },
	{ id: "email", label: "Email Address", type: "email", required: true },
	{ id: "phone", label: "Phone Number", type: "tel", required: true },
];

const Employment = () => {
	const [postings, setPostings] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedPosting, setSelectedPosting] = useState(null);
	const [formData, setFormData] = useState({});
	const [currentStep, setCurrentStep] = useState(0);
	const [successMessage, setSuccessMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");

	const theme = useTheme();

	useEffect(() => {
		const fetchPostings = async () => {
			setLoading(true);
			const { data, error } = await supabase
				.from("job_application_postings")
				.select("*")
				.order("display_order", { ascending: true })
				.order("created_at", { ascending: true });

			if (error) {
				setErrorMessage(
					error.message || "Unable to load employment opportunities right now.",
				);
				setPostings([]);
			} else {
				setPostings(data || []);
			}
			setLoading(false);
		};

		fetchPostings();
	}, []);

	const schema = useMemo(() => {
		if (!selectedPosting) return [];
		return [
			...applicantFields,
			...((selectedPosting.questions || []).map((question) => ({
				...question,
				id: question.id || question.label,
			})) ?? []),
		];
	}, [selectedPosting]);

	const getFieldKey = (field) => field.id || field.name;

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleNext = () => {
		const currentField = schema[currentStep];
		const fieldKey = getFieldKey(currentField);
		if (currentField.required && !formData[fieldKey]?.trim()) {
			setErrorMessage(
				`Please fill out the required field: ${currentField.label}`,
			);
			return;
		}
		setErrorMessage("");
		setCurrentStep((prev) => prev + 1);
	};

	const handleBack = () => {
		setErrorMessage("");
		setCurrentStep((prev) => prev - 1);
	};

	const handleCancel = () => {
		setSelectedPosting(null);
		setFormData({});
		setCurrentStep(0);
		setErrorMessage("");
	};

	const handleSubmit = async () => {
		for (const field of schema) {
			const fieldKey = getFieldKey(field);
			if (field.required && !formData[fieldKey]?.trim()) {
				setErrorMessage(`Please fill out the required field: ${field.label}`);
				return;
			}
		}

		try {
			const response = await axios.post(
				`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/submit-job-application`,
				{
					position: selectedPosting.position,
					formData,
				},
				{
					headers: {
						Authorization:
							"Bearer " + process.env.REACT_APP_SUPABASE_ANON_KEY,
						"Content-Type": "application/json",
					},
				},
			);

			if (response.status === 200) {
				setSuccessMessage("Your application has been submitted successfully!");
				setFormData({});
				setCurrentStep(0);
				setSelectedPosting(null);
				setErrorMessage("");
				setTimeout(() => {
					setSuccessMessage("");
				}, 5000);
			} else {
				setErrorMessage(response.data.error || "Failed to submit application.");
			}
		} catch (error) {
			setErrorMessage(
				error.response?.data?.error ||
					"An error occurred while submitting your application.",
			);
		}
	};

	return (
		<Container maxWidth="md">
			<Box py={4}>
				{!selectedPosting && (
					<Box>
						<Typography variant="h4" gutterBottom>
							Employment Opportunities
						</Typography>
						<Typography variant="body1" paragraph>
							Join our team! Check out the open positions below and apply today.
						</Typography>
					</Box>
				)}

				{loading ? (
					<Box display="flex" justifyContent="center" py={6}>
						<CircularProgress />
					</Box>
				) : !selectedPosting ? (
					postings.length > 0 ? (
						<TableContainer component={Paper}>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>Position</TableCell>
										<TableCell>Status</TableCell>
										<TableCell>Action</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{postings.map((details) => (
										<TableRow key={details.id}>
											<TableCell>{details.position}</TableCell>
											<TableCell>
												{details.open_for_apps ? "Open" : "Closed"}
											</TableCell>
											<TableCell>
												{details.open_for_apps && (
													<Button
														variant="contained"
														color="primary"
														onClick={() => {
															setSelectedPosting(details);
															setFormData({});
															setCurrentStep(0);
															setErrorMessage("");
														}}
													>
														Apply
													</Button>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					) : (
						<Alert severity="info">
							There are no employment opportunities available right now.
						</Alert>
					)
				) : (
					<Box>
						<Typography variant="h5" gutterBottom>
							{selectedPosting.position} Application
						</Typography>
						<Typography variant="body2" sx={{ mb: 3, whiteSpace: "pre-line" }}>
							{selectedPosting.description}
						</Typography>
						<Box mb={3}>
							<Typography variant="body2" sx={{ mb: 1 }}>
								Question {currentStep + 1} of {schema.length}
							</Typography>

							<Box
								sx={{
									height: 8,
									borderRadius: 4,
									backgroundColor: "#e0e0e0",
									overflow: "hidden",
								}}
							>
								<Box
									sx={{
										height: "100%",
										width: `${((currentStep + 1) / schema.length) * 100}%`,
										backgroundColor: theme.palette.primary.main,
										transition: "width 0.3s ease",
									}}
								/>
							</Box>
						</Box>
						<Box mt={4}>
							{schema[currentStep] && (
								<TextField
									fullWidth
									label={schema[currentStep].label}
									name={getFieldKey(schema[currentStep])}
									type={
										schema[currentStep].type === "textarea"
											? "text"
											: schema[currentStep].type
									}
									value={formData[getFieldKey(schema[currentStep])] || ""}
									onChange={handleChange}
									required={schema[currentStep].required}
									multiline={schema[currentStep].type === "textarea"}
									rows={schema[currentStep].type === "textarea" ? 4 : 1}
									InputLabelProps={
										schema[currentStep].type === "date"
											? { shrink: true }
											: undefined
									}
								/>
							)}
							{errorMessage && (
								<Alert severity="error" sx={{ mt: 2, mb: 2 }}>
									{errorMessage}
								</Alert>
							)}
							<Box mt={2} display="flex" justifyContent="space-between" gap={1}>
								<Button
									variant="outlined"
									onClick={handleCancel}
									disabled={currentStep !== 0}
								>
									Cancel
								</Button>
								<Button
									variant="outlined"
									onClick={handleBack}
									disabled={currentStep === 0}
								>
									Back
								</Button>
								{currentStep < schema.length - 1 ? (
									<Button variant="contained" onClick={handleNext}>
										Next
									</Button>
								) : (
									<Button
										variant="contained"
										color="primary"
										onClick={handleSubmit}
									>
										Submit
									</Button>
								)}
							</Box>
						</Box>
					</Box>
				)}

				{successMessage && (
					<Alert severity="success" sx={{ mt: 2, mb: 2 }}>
						{successMessage}
					</Alert>
				)}
				{errorMessage && !selectedPosting && (
					<Alert severity="error" sx={{ mt: 2, mb: 2 }}>
						{errorMessage}
					</Alert>
				)}
			</Box>
		</Container>
	);
};

export default Employment;
